import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Headphones, Play, Loader2, User, Globe, Heart, Languages, Check, Star, RotateCcw } from 'lucide-react';
import { librivoxApi, LibriVoxBook, LibriVoxSection } from './librivoxApi';
import AudiobookPlayer from './AudiobookPlayer';
import { useStreakCounter } from '../hooks/useStreakCounter';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { AppBackButton } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { librivoxProgressService, LibrivoxProgress } from './librivoxProgressService';
import { translateLongTextGoogleFirst } from '../services/translationService';
import T from '../components/T';

const ease = [0.22, 1, 0.36, 1] as const;

// Texto puro da sinopse (o LibriVox manda HTML)
const htmlToText = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = sanitizeHtml(html || '');
  div.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
  div.querySelectorAll('p').forEach(p => p.append('\n'));
  return (div.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

const LIBRIVOX_LANG: Record<string, string> = {
  english: 'en', french: 'fr', german: 'de', italian: 'it', spanish: 'es', portuguese: 'pt', russian: 'ru', japanese: 'ja', chinese: 'zh', hungarian: 'hu', dutch: 'nl', latin: 'la',
};

const AudiobookDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [book, setBook] = useState<LibriVoxBook | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState<LibriVoxSection | null>(null);
  const [startAt, setStartAt] = useState(0);
  const [progress, setProgress] = useState<LibrivoxProgress | null>(() => (id ? librivoxProgressService.cached(id) : null));
  const [favBusy, setFavBusy] = useState(false);
  const [translated, setTranslated] = useState<{ text: string; source: 'google' | 'offline-nllb' } | null>(null);
  const [showTranslated, setShowTranslated] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(false);
  useStreakCounter(!!currentSection);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const data = await librivoxApi.getAudiobookById(id);
        setBook(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
    if (id) librivoxProgressService.get(id).then(p => p && setProgress(p));
  }, [id]);

  const sourceLang = useMemo(() => (book ? LIBRIVOX_LANG[(book.language || '').toLowerCase()] : undefined), [book]);
  const targetLang = (i18n.language || 'pt').split('-')[0];
  const plainDescription = useMemo(() => (book ? htmlToText(book.description) : ''), [book]);

  const handleTranslate = async () => {
    if (translated) { setShowTranslated(s => !s); return; }
    setTranslating(true);
    setTranslateError(false);
    const r = await translateLongTextGoogleFirst(plainDescription, targetLang, sourceLang);
    setTranslating(false);
    if (r) { setTranslated({ text: r.translation, source: r.source }); setShowTranslated(true); }
    else setTranslateError(true);
  };

  const toggleFavorite = async () => {
    if (!book || favBusy) return;
    const next = !progress?.is_favorited;
    setProgress(p => (p ? { ...p, is_favorited: next } : ({ librivox_id: String(book.id), is_favorited: next, completed_sections: [], favorite_sections: [], total_sections: book.sections?.length || 0, current_section: null, position_seconds: 0, section_duration: 0, progress_percentage: 0 } as LibrivoxProgress)));
    setFavBusy(true);
    try {
      const item = await librivoxProgressService.toggleFavorite(book, next);
      if (item) setProgress(item);
    } catch {
      setProgress(p => (p ? { ...p, is_favorited: !next } : p));
    } finally {
      setFavBusy(false);
    }
  };

  const toggleSectionFavorite = async (e: React.MouseEvent, section: LibriVoxSection) => {
    e.stopPropagation();
    if (!book) return;
    const n = Number(section.section_number);
    const isFav = !!progress?.favorite_sections?.includes(n);
    setProgress(p => (p ? { ...p, favorite_sections: isFav ? p.favorite_sections.filter(x => x !== n) : [...p.favorite_sections, n] } : p));
    try {
      const item = await librivoxProgressService.toggleSectionFavorite(book, n, !isFav);
      if (item) setProgress(item);
    } catch { /* mantém o estado otimista; volta a sincronizar na próxima leitura */ }
  };

  const openSection = (section: LibriVoxSection, resumeAt = 0) => {
    setStartAt(resumeAt);
    setCurrentSection(section);
  };

  // Salva a posição (o player chama de tempos em tempos, ao pausar e ao terminar)
  const lastSaveRef = useRef(0);
  const handleProgress = useCallback((pos: number, dur: number, force = false) => {
    if (!book || !currentSection) return;
    const now = Date.now();
    if (!force && now - lastSaveRef.current < 10000) return;
    lastSaveRef.current = now;
    librivoxProgressService.saveProgress(book, currentSection, pos, dur).then(item => item && setProgress(item));
  }, [book, currentSection]);

  const handleEnded = useCallback((dur: number) => {
    if (!book || !currentSection) return;
    librivoxProgressService.saveProgress(book, currentSection, dur, dur, true).then(item => item && setProgress(item));
    // segue para a próxima seção automaticamente
    const idx = book.sections?.findIndex(s => s.section_number === currentSection.section_number) ?? -1;
    const next = idx >= 0 ? book.sections?.[idx + 1] : undefined;
    if (next) openSection(next, 0);
  }, [book, currentSection]);

  if (loading) return (
    <div className="h-[70vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="animate-spin text-muted" size={32} />
      <p className="text-muted uppercase font-mono text-[11px] tracking-[0.14em]">{t('audiobooks.loading_book', 'Carregando obra...')}</p>
    </div>
  );

  if (!book) return (
    <div className="max-w-4xl mx-auto space-y-8">
      <AppBackButton fallbackTo="/audiobooks" />
      <div className="p-20 text-center text-muted">{t('audiobooks.book_not_found', 'Livro não encontrado.')}</div>
    </div>
  );

  const total = book.sections?.length || 0;
  const done = progress?.completed_sections?.length || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const resumeSection = progress?.current_section != null ? book.sections?.find(s => Number(s.section_number) === progress.current_section) : undefined;
  const author = [book.authors?.[0]?.first_name, book.authors?.[0]?.last_name].filter(Boolean).join(' ');

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-48">
      <div className="flex items-center justify-between">
        <AppBackButton fallbackTo="/audiobooks" />
        <button
          onClick={toggleFavorite}
          disabled={favBusy}
          className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${progress?.is_favorited ? 'bg-tint-rose text-ink' : 'bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone'}`}
          title={progress?.is_favorited ? 'Remover dos favoritos' : 'Favoritar'}
          aria-label={progress?.is_favorited ? 'Remover dos favoritos' : 'Favoritar'}
        >
          <AnimatedIcon motion="pop" active={!!progress?.is_favorited}>
            <Heart size={18} strokeWidth={1.75} fill={progress?.is_favorited ? 'currentColor' : 'none'} />
          </AnimatedIcon>
        </button>
      </div>

      {/* Cabeçalho: capa com brilho, título editorial e progresso */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }} className="relative flex flex-col md:flex-row gap-8 md:gap-10 items-center md:items-start">
        <div className="relative w-56 md:w-64 shrink-0">
          {book.coverart_thumbnail && (
            <img aria-hidden src={book.coverart_thumbnail} className="absolute inset-4 w-[calc(100%-2rem)] h-[calc(100%-2rem)] object-cover rounded-[28px] blur-2xl opacity-50 scale-110" alt="" />
          )}
          <div className="relative aspect-square bg-tint-stone rounded-[28px] overflow-hidden ring-1 ring-black/[0.06] dark:ring-white/[0.06]">
            <img
              src={book.coverart_thumbnail}
              className="w-full h-full object-cover"
              alt={book.title}
              onError={(e) => {
                const img = e.currentTarget;
                if (img.src.includes('archive.org')) {
                  img.src = `https://covers.openlibrary.org/b/title/${encodeURIComponent(book.title)}-L.jpg`;
                } else if (img.src.includes('openlibrary.org')) {
                  img.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(book.title)}&background=4f46e5&color=fff&size=512&font-size=0.35&bold=true`;
                } else {
                  img.onerror = null;
                }
              }}
            />
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-5 text-center md:text-left">
          <div>
            <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted flex items-center justify-center md:justify-start gap-2">
              <AnimatedIcon motion="pop"><Headphones size={13} strokeWidth={1.75} /></AnimatedIcon> audiobook · librivox
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-medium tracking-[-0.035em] text-ink leading-[1.05] mt-2 break-words">
              {book.title}<span className="text-primary">.</span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
            {author && (
              <span className="h-9 px-3.5 rounded-full bg-tint-lavender text-ink text-sm flex items-center gap-2"><User size={14} strokeWidth={1.75} /> {author}</span>
            )}
            <span className="h-9 px-3.5 rounded-full bg-tint-sky text-ink text-sm flex items-center gap-2"><Globe size={14} strokeWidth={1.75} /> {book.language}</span>
            {total > 0 && (
              <span className="h-9 px-3.5 rounded-full bg-ink/[0.05] text-ink text-sm flex items-center gap-2 font-mono text-xs">{done}/{total} seções · {pct}%</span>
            )}
          </div>

          {total > 0 && (
            <div className="w-full max-w-md mx-auto md:mx-0 h-1 rounded-full bg-ink/[0.07] overflow-hidden">
              <motion.div className="h-full bg-ok rounded-full" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease }} />
            </div>
          )}

          {resumeSection && !currentSection && (
            <button
              onClick={() => openSection(resumeSection, progress?.position_seconds || 0)}
              className="group h-12 pl-1.5 pr-5 rounded-full bg-primary text-slate-900 text-sm inline-flex items-center gap-3 hover:scale-[1.02] active:scale-95 transition-transform"
            >
              <span className="w-9 h-9 rounded-full bg-surface/15 flex items-center justify-center"><AnimatedIcon motion="spin"><RotateCcw size={15} strokeWidth={1.75} /></AnimatedIcon></span>
              Continuar: {resumeSection.title}
            </button>
          )}
        </div>
      </motion.div>

      {/* Sinopse com tradução */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease }} className="bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] p-6 md:p-8 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">sinopse</span>
          <button
            onClick={handleTranslate}
            disabled={translating}
            className={`h-9 pl-2.5 pr-3.5 rounded-full text-xs flex items-center gap-1.5 transition-colors ${showTranslated ? 'bg-ink text-surface' : 'bg-ink/[0.05] hover:bg-ink/[0.09] text-ink'}`}
            title={showTranslated ? 'Ver original' : 'Traduzir'}
          >
            {translating ? <Loader2 size={13} className="animate-spin" /> : <AnimatedIcon motion="flip" active={showTranslated}><Languages size={13} strokeWidth={1.75} /></AnimatedIcon>}
            {translating ? 'Traduzindo…' : showTranslated ? 'Ver original' : 'Traduzir'}
          </button>
        </div>
        <AnimatePresence mode="wait">
          {showTranslated && translated ? (
            <motion.div key="tr" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-3">
              <div className="text-[15px] text-ink/80 leading-relaxed max-h-72 overflow-y-auto pr-3 custom-scrollbar whitespace-pre-line">{translated.text}</div>
              <span className="text-[10px] uppercase font-mono tracking-[0.12em] text-muted">
                traduzido {translated.source === 'google' ? 'com o google tradutor' : 'com o modelo local do aparelho'}
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="orig"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="text-[15px] text-ink/80 leading-relaxed max-h-72 overflow-y-auto pr-3 custom-scrollbar"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(book.description) }}
            />
          )}
        </AnimatePresence>
        {translateError && <p className="text-xs text-bad"><T k="ui.ab.translateError" /></p>}
      </motion.section>

      {/* Capítulos e seções */}
      <section className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h3 className="font-display text-2xl font-medium tracking-[-0.03em] text-ink">{t('audiobooks.chapters_and_sections', 'Capítulos e Seções')}<span className="text-primary">.</span></h3>
          <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">{total} seções</span>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {book.sections?.map((section, i) => {
            const n = Number(section.section_number);
            const active = currentSection?.section_number === section.section_number;
            const completed = !!progress?.completed_sections?.includes(n);
            const isResume = !active && progress?.current_section === n && !completed;
            const fav = !!progress?.favorite_sections?.includes(n);
            return (
              <motion.div
                key={section.section_number}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(i, 12) * 0.025 }}
                onClick={() => openSection(section, isResume ? progress?.position_seconds || 0 : 0)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') openSection(section); }}
                className={`group flex items-center justify-between gap-3 p-2.5 pr-3 rounded-full cursor-pointer transition-colors ${active ? 'bg-ink text-surface' : 'bg-surface border border-black/[0.06] dark:border-white/[0.06] hover:bg-tint-stone text-ink'}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-xs font-mono transition-colors ${active ? 'bg-surface/15' : completed ? 'bg-tint-sage' : isResume ? 'bg-tint-butter' : 'bg-ink/[0.05]'}`}>
                    {active ? <Headphones size={16} className="animate-pulse" /> : completed ? <Check size={16} strokeWidth={2} /> : (
                      <>
                        <span className="group-hover:hidden">{String(section.section_number).padStart(2, '0')}</span>
                        <Play size={14} fill="currentColor" className="hidden group-hover:block ml-0.5" />
                      </>
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{section.title}</p>
                    <p className={`text-[10px] uppercase tracking-[0.12em] font-mono ${active ? 'opacity-70' : 'text-muted'}`}>
                      {t('audiobooks.section', 'Seção')} {section.section_number} · {section.playtime}{isResume ? ' · parou aqui' : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={e => toggleSectionFavorite(e, section)}
                  className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-colors ${fav ? 'text-warn' : active ? 'text-surface/50 hover:text-surface' : 'text-ink/25 hover:text-ink/60'}`}
                  title={fav ? 'Remover seção dos favoritos' : 'Favoritar seção'}
                  aria-label={fav ? 'Remover seção dos favoritos' : 'Favoritar seção'}
                >
                  <AnimatedIcon motion="spin" active={fav}><Star size={15} strokeWidth={1.75} fill={fav ? 'currentColor' : 'none'} /></AnimatedIcon>
                </button>
              </motion.div>
            );
          })}
        </div>
      </section>

      {currentSection && (
        <AudiobookPlayer
          url={currentSection.listen_url}
          title={currentSection.title}
          bookTitle={book.title}
          startAt={startAt}
          onProgress={handleProgress}
          onEnded={handleEnded}
          onClose={() => setCurrentSection(null)}
        />
      )}
    </div>
  );
};

export default AudiobookDetailPage;
